/* cali shipping protection (2026-09-19)
 *
 * The in-house replacement for the Navidium ("Averra") widget. Everything the
 * vendor's two CDN scripts did on this storefront is here: quote a fee from the
 * merchandise subtotal, pick the matching variant of
 * `navidium-shipping-protection`, add or remove exactly one unit of it, and
 * keep that line in step with every later change to the cart.
 *
 * WHY THERE IS EXACTLY ONE PLACE THAT ADDS
 * ----------------------------------------
 * The vendor charged ~28 of our orders twice (about €210 between June and
 * September 2026). Root cause, reproduced on the live drawer 2026-09-09: their
 * widget bound a jQuery `.one()` handler delegated to
 * '[name="checkout"], [name="checkout"] *'. jQuery builds the whole delegated
 * handler queue BEFORE running any of it, so a single click on the text inside
 * the checkout button ran that "one-time" handler once per matching ancestor of
 * the click target — the span, then the button. Each run called their
 * addProtection() = POST /cart/add.js for the same variant a few milliseconds
 * apart, and the customer paid for two units. `assets/cali-cart-sync.js` had to
 * grow a de-duplicating fetch wrapper to patch it from outside.
 *
 * The lesson is not "de-duplicate adds", it is "have one adder". So:
 *
 * - The ONLY thing that writes protection into the cart is `write()` below, and
 *   it is single-flight: a second call while one is in flight is dropped, not
 *   queued. There is no add on the checkout click, no add on a click trigger,
 *   no add on page load. A cart write happens on exactly two occasions: the
 *   customer moves the checkbox (or, with checkout_style 'protected', presses
 *   "Check out with protection" or "Check out without protection"), or the
 *   cart's merchandise changed and the line that is already there is now the
 *   wrong tier.
 * - There is no second adder anywhere else in the theme. cali-cart-sync.js was
 *   slimmed the same day and now only reads /cart.js and broadcasts it; the
 *   vendor's `nvd_init` re-run, the localStorage `nvdVariant` swap and the
 *   add-dedupe window all went with the vendor, because with one adder they
 *   have nothing left to protect against.
 * - Checkout is not intercepted. The button is only ever DISABLED for the
 *   duration of a write (see holdCheckout), because Motion's native checkout is
 *   a form POST with positional `updates[]` and submitting it mid-write would
 *   post stale line positions.
 *
 * STATE
 * -----
 * The cart is the only source of truth. "Protected" means a protection line is
 * in /cart.js; the checkbox mirrors that, it does not drive it. localStorage is
 * never consulted (the vendor's did, and could claim protection was in a cart
 * that had none). sessionStorage holds one flag, "the customer unticked this
 * session", and it is only read when default_opt_in is on — it can never cause
 * an add.
 *
 * The tier rule is mirrored byte-for-byte in
 * snippets/cali-shipping-protection.liquid, which paints the first frame; see
 * that file's header for the rule and for why the clamp is currency-dependent
 * there and exact here.
 */
(function () {
  'use strict';

  if (window.__caliShippingProtection) return;
  window.__caliShippingProtection = true;

  var cfgEl = document.getElementById('CaliShippingProtectionConfig');
  var CFG = null;
  try {
    CFG = JSON.parse((cfgEl && cfgEl.textContent) || 'null');
  } catch (err) {
    CFG = null;
  }
  if (!CFG || CFG.v !== 1) return;

  var PROP_DISCLAIMER = 'Disclaimer'; /* the property the vendor wrote; kept so
                                         old and new orders read identically */
  var PROP_FLAG = '_cali_sp'; /* hidden; HQ tells in-house from vendor orders */
  var DECLINED_KEY = 'cali_sp_declined';
  var RELEASE_FALLBACK_MS = 2000;

  var state = { tier: null, autoAdded: false };
  var writing = false;

  /* ---- helpers ---- */

  function root() {
    return document.querySelector('[data-cali-sp]');
  }

  function isProtection(item) {
    return !!(item && item.handle && item.handle.indexOf(CFG.handle) !== -1);
  }

  function formatCents(cents) {
    var sync = window.caliCartSync;
    if (sync && typeof sync.formatCents === 'function') {
      return sync.formatCents(cents);
    }
    return (Math.round(cents) / 100).toFixed(2);
  }

  function json(r) {
    return r.json();
  }

  function getCart() {
    var sync = window.caliCartSync;
    if (sync && typeof sync.getCart === 'function') return sync.getCart();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(json);
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(json);
  }

  function declined() {
    try {
      return sessionStorage.getItem(DECLINED_KEY) === '1';
    } catch (err) {
      return false;
    }
  }

  function setDeclined(v) {
    try {
      if (v) sessionStorage.setItem(DECLINED_KEY, '1');
      else sessionStorage.removeItem(DECLINED_KEY);
    } catch (err) {}
  }

  /* ---- the tier rule ----
     goods = cart.total_price − the protection lines, 0 when nothing left in the
     cart requires shipping. target = round(goods × rate / 100) clamped to
     [min, max] converted at the presentment rate. The chosen variant is the one
     whose presented price is closest to target, ties to the lower price, so the
     customer always pays an exact variant price. */

  function goodsOf(cart) {
    var protection = 0;
    var shippable = false;
    (cart.items || []).forEach(function (it) {
      if (isProtection(it)) {
        protection += it.final_line_price;
      } else if (it.requires_shipping) {
        shippable = true;
      }
    });
    if (!shippable) return 0;
    var goods = cart.total_price - protection;
    return goods > 0 ? goods : 0;
  }

  function presentmentRate() {
    try {
      var r = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate);
      if (r > 0) return r;
    } catch (err) {}
    return 1;
  }

  function pickTier(goods) {
    if (!CFG.enabled) return null;
    if (!CFG.tiers || !CFG.tiers.length) return null;
    if (goods <= 0 || goods < (CFG.minGoodsMinor || 0)) return null;

    var rate = presentmentRate();
    var min = Math.round(CFG.minFeeMinor * rate);
    var max = Math.round(CFG.maxFeeMinor * rate);
    var target = Math.round((goods * CFG.ratePct) / 100);
    if (target < min) target = min;
    if (target > max) target = max;

    var best = null;
    var bestDelta = Infinity;
    for (var i = 0; i < CFG.tiers.length; i++) {
      var price = CFG.tiers[i][1];
      var delta = Math.abs(price - target);
      if (delta < bestDelta || (delta === bestDelta && price < best[1])) {
        best = CFG.tiers[i];
        bestDelta = delta;
      }
    }
    return best ? { id: best[0], price: best[1] } : null;
  }

  /* ---- checkout hold ----
     A protection write reorders the cart's lines, and Motion's native checkout
     posts positional `updates[]`. The button is disabled from the first byte of
     a write until Motion has rebuilt [data-products] from cart.ajax (which is
     what makes the positions current again), with a 2 s fallback so a failed
     rebuild can never strand a customer on a dead button. */

  var held = false;
  var holdTimer = null;
  var observer = null;

  function checkoutButtons() {
    return document.querySelectorAll('[name="checkout"]');
  }

  function holdCheckout() {
    if (window.caliCartSync && window.caliCartSync.runMutation) return;
    if (held) return;
    held = true;
    Array.prototype.forEach.call(checkoutButtons(), function (b) {
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
    });
    var products = document.querySelector('[data-products]');
    if (products && window.MutationObserver) {
      observer = new MutationObserver(function () {
        releaseCheckout();
      });
      observer.observe(products, { childList: true });
    }
    holdTimer = setTimeout(releaseCheckout, RELEASE_FALLBACK_MS);
  }

  function releaseCheckout() {
    if (window.caliCartSync && window.caliCartSync.runMutation) return;
    if (!held) return;
    held = false;
    if (holdTimer) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    Array.prototype.forEach.call(checkoutButtons(), function (b) {
      b.disabled = false;
      b.removeAttribute('aria-busy');
    });
  }

  function rebuild() {
    try {
      document.dispatchEvent(new CustomEvent('cart:build'));
    } catch (err) {}
  }

  /* ---- the one adder ---- */

  function write(fn) {
    if (writing) return Promise.resolve(null);
    writing = true;
    var runner = window.caliCartSync && window.caliCartSync.runMutation;
    if (!runner) holdCheckout();
    return (runner ? runner(fn) : fn()).then(
      function (res) {
        writing = false;
        if (!runner) rebuild();
        return res;
      },
      function (err) {
        writing = false;
        if (!runner) { releaseCheckout(); rebuild(); }
        return null;
      }
    );
  }

  function addTier(tier) {
    var properties = {};
    properties[PROP_DISCLAIMER] = CFG.disclaimer || '';
    properties[PROP_FLAG] = '1';
    return post('/cart/add.js', {
      id: tier.id,
      quantity: 1,
      properties: properties
    });
  }

  function updateLines(updates) {
    return post('/cart/update.js', { updates: updates });
  }

  function clearCart() {
    return post('/cart/clear.js', {});
  }

  /* One unit of `tier`, nothing else.
     If a line for that exact variant already exists we address every protection
     line BY KEY in a single update (its quantity to 1, the others to 0) — mixing
     line keys and variant ids in one call removes the variant altogether.
     If it does not, we add first and then zero the keys captured before the add,
     so the cart is never momentarily unprotected and a protection-only cart is
     never created (Shopify answers such a cart 422 "cart is not valid"). */
  function swapTo(tier, lines) {
    var existing = null;
    lines.forEach(function (l) {
      if (!existing && String(l.variant_id) === String(tier.id)) existing = l;
    });

    var updates = {};
    if (existing) {
      lines.forEach(function (l) {
        updates[l.key] = l === existing ? 1 : 0;
      });
      return write(function () {
        return updateLines(updates);
      });
    }

    lines.forEach(function (l) {
      updates[l.key] = 0;
    });
    return write(function () {
      return addTier(tier).then(function () {
        return updateLines(updates);
      });
    });
  }

  function removeAll(cart, lines) {
    var merchandise = (cart.items || []).length - lines.length;
    if (merchandise === 0) {
      return write(function () {
        return clearCart();
      });
    }
    var updates = {};
    lines.forEach(function (l) {
      updates[l.key] = 0;
    });
    return write(function () {
      return updateLines(updates);
    });
  }

  /* ---- one line, fee included (cali 2026-09-20) ----
     The sentence that carries the fee ("Contre les dommages & le vol pour
     €2,00") must stay on ONE line in every language. The stylesheet size is
     12px; when the sentence is wider than its column the size steps down in
     half pixels to a 9.5px floor, and the inline fee (font-size: inherit)
     shrinks with it. If even the floor does not fit, the line wraps as before:
     a font that small is worse than a second line. Runs at first paint, on
     every render (the fee text changes the width), when the theme opens the
     drawer (the column has no width while the drawer is display:none) and on
     window resize. Without this script the sentence simply wraps, as it did
     before. */
  var FIT_FLOOR = 9.5;

  function fitBody(el) {
    var body = el && el.querySelector('.cali-sp__body--fee');
    var box = body && body.parentNode;
    if (!box) return;
    var avail = box.clientWidth;
    if (!avail) return;
    body.style.fontSize = '';
    body.style.whiteSpace = 'nowrap';
    var natural = body.scrollWidth;
    if (natural <= avail) return;
    var base = parseFloat(window.getComputedStyle(body).fontSize) || 12;
    var size = Math.floor((base * avail / natural) * 2) / 2;
    if (size < FIT_FLOOR) size = FIT_FLOOR;
    body.style.fontSize = size + 'px';
    while (body.scrollWidth > avail && size > FIT_FLOOR) {
      size -= 0.5;
      body.style.fontSize = size + 'px';
    }
    if (body.scrollWidth > avail) {
      body.style.fontSize = '';
      body.style.whiteSpace = '';
    }
  }

  /* ---- render / reconcile ---- */

  function render(cart) {
    var el = root();
    var tier = pickTier(goodsOf(cart));
    state.tier = tier;
    if (!el) return;

    if (!tier) {
      /* An EMPTY cart under the protected checkout keeps the card in place,
         awaiting its numbers (see the Liquid): the drawer shows its empty state
         anyway, and the next add then opens on the card, never on Motion's
         button. A cart with items and nothing to protect hides it. */
      if (CFG.style === 'protected' && CFG.enabled && !(cart.item_count > 0)) {
        el.classList.remove('cali-sp--hidden');
        el.removeAttribute('aria-hidden');
        el.classList.add('cali-sp--awaiting');
        nativeCheckout(el, true);
        return;
      }
      el.classList.remove('cali-sp--awaiting');
      el.classList.add('cali-sp--hidden');
      el.setAttribute('aria-hidden', 'true');
      nativeCheckout(el, false);
      return;
    }
    el.classList.remove('cali-sp--hidden');
    el.classList.remove('cali-sp--awaiting');
    el.removeAttribute('aria-hidden');
    el.setAttribute('data-cali-sp-variant', tier.id);

    var fees = el.querySelectorAll('[data-cali-sp-fee]');
    var text = formatCents(tier.price);
    Array.prototype.forEach.call(fees, function (fee) {
      if (fee.textContent !== text) fee.textContent = text;
    });

    if (CFG.style === 'protected') {
      var total = el.querySelector('[data-cali-sp-total]');
      var sum = formatCents(baseTotal(cart) + tier.price);
      if (total && total.textContent !== sum) total.textContent = sum;
      nativeCheckout(el, true);
    }

    fitBody(el);

    var box = el.querySelector('[data-cali-sp-check]');
    if (box && !writing) {
      var on = (cart.items || []).some(isProtection);
      if (box.checked !== on) box.checked = on;
      box.disabled = false;
    }
  }

  function reconcile(cart) {
    if (writing) return Promise.resolve(null);
    var lines = (cart.items || []).filter(isProtection);
    var tier = state.tier;

    if (!tier) {
      /* Disabled, wholesale, no shippable merchandise, or a cart discounted to
         zero: a protection line here is junk and is removed. */
      if (!lines.length) return Promise.resolve(null);
      return removeAll(cart, lines);
    }

    if (!lines.length) {
      /* With the protected checkout the customer chooses at the button, so
         nothing is ever put in the cart ahead of that choice. */
      if (CFG.style !== 'protected' && CFG.defaultOptIn && !declined() && !state.autoAdded) {
        state.autoAdded = true;
        return write(function () {
          return addTier(tier);
        });
      }
      return Promise.resolve(null);
    }

    var correct =
      lines.length === 1 &&
      lines[0].quantity === 1 &&
      String(lines[0].variant_id) === String(tier.id);
    if (correct) return Promise.resolve(null);

    return swapTo(tier, lines);
  }

  /* ---- the customer's own toggle ----
     Re-reads the cart rather than trusting the last render, so a click that
     lands while another tab or an app has changed the cart still writes the
     tier the cart actually deserves. */

  function onToggle(box) {
    var want = box.checked;
    box.disabled = true;
    setDeclined(!want);

    getCart()
      .then(function (cart) {
        var lines = (cart.items || []).filter(isProtection);
        if (!want) {
          if (!lines.length) return null;
          return removeAll(cart, lines);
        }
        var tier = pickTier(goodsOf(cart));
        state.tier = tier;
        if (!tier) {
          box.checked = false;
          return null;
        }
        if (lines.length) return swapTo(tier, lines);
        return write(function () {
          return addTier(tier);
        });
      })
      .then(
        function () {
          box.disabled = false;
        },
        function () {
          box.disabled = false;
        }
      );
  }

  document.addEventListener('change', function (evt) {
    var box = evt.target;
    if (!box || !box.hasAttribute || !box.hasAttribute('data-cali-sp-check')) return;
    onToggle(box);
  });

  /* ---- protected checkout (cali 2026-10-05) ----
     checkout_style 'protected' replaces the toggle with a card: its button
     checks out WITH protection, the link under it WITHOUT. Either one brings
     the cart to the state the customer chose through the one adder above,
     reads /cart.js back to confirm it, and only then opens checkout.

     Checkout is opened by URL, never by submitting Motion's form: after a
     write the form's positional updates[] still describe the lines of before
     (see holdCheckout), and submitting them would apply the merchandise
     quantities to the wrong lines. Motion's own button stays in the DOM,
     hidden while the card shows, and is only clicked to let Motion run its
     terms check when that setting is on.

     A state that cannot be confirmed after three tries leaves the customer on
     the cart with the buttons enabled again. Nobody is sent to pay for
     protection they declined, or without the protection they asked for. */

  function baseTotal(cart) {
    var total = cart.total_price || 0;
    (cart.items || []).forEach(function (it) {
      if (isProtection(it)) total -= it.final_line_price;
    });
    return total > 0 ? total : 0;
  }

  /* While the card shows, Motion's checkout button (the next sibling of the
     options block) is hidden. The stylesheet does this with :has() from the
     first paint; the class is the same thing for browsers without :has(). */
  function nativeCheckout(el, hide) {
    var wrap = el && el.closest ? el.closest('.cali-cart-options') : null;
    var next = wrap && wrap.nextElementSibling;
    if (!next || !next.classList.contains('cart__checkout-wrapper')) return;
    if (hide) next.classList.add('cali-sp-native-off');
    else next.classList.remove('cali-sp-native-off');
  }

  function freshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(json);
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /* The state the customer chose. A cart with nothing to protect (no tier)
     must hold no protection line, whichever button was pressed. */
  function matches(cart, want) {
    var lines = (cart.items || []).filter(isProtection);
    var tier = want ? pickTier(goodsOf(cart)) : null;
    if (!tier) return lines.length === 0;
    return lines.length === 1 && lines[0].quantity === 1 && String(lines[0].variant_id) === String(tier.id);
  }

  function bring(cart, want) {
    var lines = (cart.items || []).filter(isProtection);
    var tier = want ? pickTier(goodsOf(cart)) : null;
    if (want) state.tier = tier;
    if (!tier) return lines.length ? removeAll(cart, lines) : Promise.resolve(null);
    if (lines.length) return swapTo(tier, lines);
    return write(function () {
      return addTier(tier);
    });
  }

  /* write() is single-flight: a press that lands while a tier swap is in
     flight is dropped, not queued. The read-back catches that and tries again. */
  function settle(want, attempt) {
    return freshCart().then(function (cart) {
      if (matches(cart, want)) return true;
      if (attempt >= 3) return false;
      return bring(cart, want)
        .then(function () {
          return wait(attempt ? 300 : 0);
        })
        .then(function () {
          return settle(want, attempt + 1);
        });
    });
  }

  function checkoutUrl() {
    var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return base.replace(/\/?$/, '/') + 'checkout';
  }

  var leaving = false;

  function busy(el, on) {
    if (!el) return;
    var controls = el.querySelectorAll('[data-cali-sp-with], [data-cali-sp-without]');
    Array.prototype.forEach.call(controls, function (b) {
      b.disabled = on;
      if (on) b.setAttribute('aria-busy', 'true');
      else b.removeAttribute('aria-busy');
    });
  }

  function protectedCheckout(want) {
    if (leaving) return;
    var el = root();
    if (!el) return;
    var form = el.closest ? el.closest('form') : null;
    var terms = form && form.querySelector('.cart__terms-checkbox');
    if (terms && !terms.checked) {
      var motion = form.querySelector('.cart__checkout');
      if (motion) motion.click();
      return;
    }
    leaving = true;
    busy(el, true);
    settle(want, 0).then(
      function (ok) {
        if (ok) {
          window.location.href = checkoutUrl();
          return;
        }
        leaving = false;
        busy(el, false);
      },
      function () {
        leaving = false;
        busy(el, false);
      }
    );
  }

  document.addEventListener('click', function (evt) {
    if (CFG.style !== 'protected') return;
    var target = evt.target && evt.target.closest ? evt.target.closest('[data-cali-sp-with], [data-cali-sp-without]') : null;
    if (!target) return;
    evt.preventDefault();
    protectedCheckout(target.hasAttribute('data-cali-sp-with'));
  });

  /* ---- the button's hover (cali 2026-10-07 sp-cta-hover-fit) ----
     On hover the theme's arrow slides in at the right of "Check out with
     protection | total" and the label and total move left to make room (the
     stylesheet, next to .cali-sp__cta). This sets how much they shrink: not at
     all when they fit beside the arrow, otherwise just enough, so the line
     never breaks. Measured as the pointer enters, the one moment the answer is
     needed: the label, the total and the drawer's width are all current then,
     and a transform never changes the widths read here. */
  function fitCta(btn) {
    var inner = btn.querySelector('.cali-sp__cta-inner');
    var natural = inner && inner.offsetWidth;
    if (!natural) return;
    var cs = window.getComputedStyle(btn);
    var room = parseFloat(cs.getPropertyValue('--cali-sp-cta-arrow')) || 0;
    var avail = btn.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0) - room;
    var fit = avail > 0 ? Math.min(1, Math.floor((avail / natural) * 1000) / 1000) : 1;
    btn.style.setProperty('--cali-sp-cta-fit', String(fit));
  }

  document.addEventListener('mouseover', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest('[data-cali-sp-with]') : null;
    if (btn && !btn.contains(evt.relatedTarget)) fitCta(btn);
  });

  /* Back from checkout, the browser can restore this page from its cache with
     the buttons still disabled. */
  window.addEventListener('pageshow', function (evt) {
    if (!evt.persisted) return;
    leaving = false;
    busy(root(), false);
  });

  /* ---- the 422 that only a protection line can cause ----
     Shopify refuses any cart mutation whose RESULT would be a cart holding
     nothing but the protection line: POST /cart/change.js {quantity:0} on the
     last merchandise line answers 422 {"message":"Your cart is not valid"} and
     the line stays. Measured on the live store 2026-09-19, and the same 422 is
     already documented in snippets/cali-cart-language.liquid, so it predates
     this widget and is not something the in-house code introduced.

     Motion's own quantityChanged alerts that message at the customer, so
     without this the drawer's "−" on the last item would pop an alert and do
     nothing — the cart could never be emptied while protection was ticked.

     The recovery is deliberately the narrowest thing that works: only for
     change/update (an add can never produce that cart), only on 422, only when
     the message is that one, only when a protection line actually exists, and
     never for our own writes. It removes ONLY the protection lines and then
     replays the original request once, so a 422 that turns out to have had
     another cause costs the customer the fee line they can re-tick and never a
     line of merchandise. The caller gets the replayed response, so its own
     bookkeeping and rebuild run exactly as on a normal success. */

  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = origFetch.apply(this, arguments);
      if (writing || !/\/cart\/(change|update)\.js/.test(url)) return promise;

      var ctx = this;
      var args = arguments;
      return promise.then(function (res) {
        if (!res || res.status !== 422) return res;
        var copy;
        try {
          copy = res.clone();
        } catch (err) {
          return res;
        }
        return copy.json().then(
          function (body) {
            var message = (body && (body.message || body.description)) || '';
            if (message.indexOf('not valid') === -1) return res;
            return dropProtection(ctx, args, res);
          },
          function () {
            return res;
          }
        );
      });
    };
  }

  function dropProtection(ctx, args, failed) {
    return getCart().then(
      function (cart) {
        var lines = (cart.items || []).filter(isProtection);
        if (!lines.length) return failed;
        var updates = {};
        lines.forEach(function (l) {
          updates[l.key] = 0;
        });
        writing = true;
        return origFetch
          .call(ctx, '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates })
          })
          .then(
            function () {
              writing = false;
              return origFetch.apply(ctx, args);
            },
            function () {
              writing = false;
              return failed;
            }
          );
      },
      function () {
        return failed;
      }
    );
  }

  /* ---- the single cart event ----
     assets/cali-cart-sync.js owns the /cart.js read and the debounce for every
     cart mutation in the theme, ours included, and hands the fresh cart over. */

  document.addEventListener('cali:cart', function (evt) {
    var cart = evt.detail && evt.detail.cart;
    if (!cart || !cart.items) return;
    render(cart);
    reconcile(cart);
  });

  /* ---- paint without waiting (cali 2026-10-05 sp-card-first-paint) ----
     cali:cart arrives 600 ms after the last cart change (cali-cart-sync
     batches the bursts of writes apps make) plus a /cart.js read, so the
     card's fee and total used to follow an Add to cart by about a second.
     The theme's own signals now repaint it at once: Motion's cart:updated
     carries the cart; an add and a drawer opening read /cart.js right away.
     Paint only: the writes (reconcile) still wait for cali:cart. A newer read
     always wins over an older one still in flight. */
  var paintSeq = 0;

  function paintNow(cart) {
    var seq = ++paintSeq;
    if (cart && cart.items) {
      render(cart);
      return;
    }
    freshCart().then(
      function (fresh) {
        if (seq === paintSeq && fresh && fresh.items && !writing) render(fresh);
      },
      function () {}
    );
  }

  document.addEventListener('cart:updated', function (evt) {
    paintNow(evt.detail && evt.detail.cart);
  });
  document.addEventListener('ajaxProduct:added', function () {
    paintNow(null);
  });
  document.addEventListener('drawerOpen', function () {
    paintNow(null);
  });

  /* First paint (cali 2026-09-20): the Liquid renders the widget before any
     cart event, and the drawer can be opened from the header icon without a
     cart mutation, so the fit must not wait for the first cali:cart. The
     theme announces every drawer opening ('drawerOpen', Motion's Drawers) and
     the drawer is display:none while closed, so that event is the first moment
     the column has a width: fit right away and once more after the slide-in.
     Size observers were tried and dropped: on this theme they missed both the
     drawer's rebuild and viewport changes. */
  function fitFirstPaint() {
    var el = root();
    if (el) fitBody(el);
    if (el && CFG.style === 'protected') nativeCheckout(el, !el.classList.contains('cali-sp--hidden'));
  }
  function onDrawerOpen() {
    window.setTimeout(fitFirstPaint, 0);
    window.setTimeout(fitFirstPaint, 350);
  }
  var fitTimer = null;
  function onResize() {
    window.clearTimeout(fitTimer);
    fitTimer = window.setTimeout(fitFirstPaint, 80);
  }
  function startFit() {
    fitFirstPaint();
    document.addEventListener('drawerOpen', onDrawerOpen);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startFit);
  } else {
    startFit();
  }
})();
